// Enrollment status configuration
// Controls landing page content and signup behavior

import { useUIStore } from '@/store/ui-store';
import type { EnrollmentStatus } from '@/types';

export type { EnrollmentStatus };

const ENROLLMENT_STATUSES: EnrollmentStatus[] = [
  'pre_enrollment',
  'enrollment_open',
  'waitlist_open',
  'enrollment_closed',
];

const DEV_OVERRIDE_KEY = 'dev_enrollment_status_override';

function isValidStatus(status: string | null | undefined): status is EnrollmentStatus {
  return ENROLLMENT_STATUSES.includes(status as EnrollmentStatus);
}

/**
 * Returns true if we're in a non-production environment.
 */
export function isNonProductionEnv(): boolean {
  return process.env.NEXT_PUBLIC_ENV !== 'production';
}

/**
 * Returns the current enrollment status (non-reactive).
 * In non-production environments, checks localStorage for a dev override first.
 * Falls back to Zustand store → env var → 'pre_enrollment'.
 */
export function getEnrollmentStatus(): EnrollmentStatus {
  // Check for dev override in non-production environments
  if (isNonProductionEnv() && typeof window !== 'undefined') {
    const override = localStorage.getItem(DEV_OVERRIDE_KEY);
    if (isValidStatus(override)) {
      return override;
    }
  }

  // Read from Zustand store (synced from API)
  const storeStatus = useUIStore.getState().currentSeason.enrollment_status;
  if (isValidStatus(storeStatus)) {
    return storeStatus;
  }

  // Env var fallback (rollout safety)
  const envStatus = process.env.NEXT_PUBLIC_ENROLLMENT_STATUS;
  if (isValidStatus(envStatus)) {
    return envStatus;
  }

  return 'pre_enrollment';
}

/**
 * Reactive hook that returns the current enrollment status.
 * Subscribes to Zustand store changes so UI updates automatically.
 */
export function useEnrollmentStatus(): EnrollmentStatus {
  const storeStatus = useUIStore((s) => s.currentSeason.enrollment_status);

  // Check for dev override in non-production environments
  if (isNonProductionEnv() && typeof window !== 'undefined') {
    const override = localStorage.getItem(DEV_OVERRIDE_KEY);
    if (isValidStatus(override)) {
      return override;
    }
  }

  if (isValidStatus(storeStatus)) {
    return storeStatus;
  }

  // Env var fallback
  const envStatus = process.env.NEXT_PUBLIC_ENROLLMENT_STATUS;
  if (isValidStatus(envStatus)) {
    return envStatus;
  }

  return 'pre_enrollment';
}

/**
 * Reactive hook that returns whether we're in waitlist mode.
 */
export function useIsWaitlistMode(): boolean {
  return useEnrollmentStatus() === 'waitlist_open';
}

/**
 * Sets a dev override for enrollment status (non-production only).
 */
export function setDevEnrollmentOverride(status: EnrollmentStatus | null): void {
  if (typeof window === 'undefined') return;
  if (status === null) {
    localStorage.removeItem(DEV_OVERRIDE_KEY);
  } else {
    localStorage.setItem(DEV_OVERRIDE_KEY, status);
  }
}

/**
 * Gets the current dev override (or null if none set).
 */
export function getDevEnrollmentOverride(): EnrollmentStatus | null {
  if (typeof window === 'undefined') return null;
  const override = localStorage.getItem(DEV_OVERRIDE_KEY);
  return isValidStatus(override) ? override : null;
}

export function isPreEnrollment(): boolean {
  return getEnrollmentStatus() === 'pre_enrollment';
}

export function isEnrollmentOpen(): boolean {
  return getEnrollmentStatus() === 'enrollment_open';
}

export function isWaitlistMode(): boolean {
  return getEnrollmentStatus() === 'waitlist_open';
}

export function isEnrollmentClosed(): boolean {
  return getEnrollmentStatus() === 'enrollment_closed';
}
